import clsx from 'clsx'
import type { PropsWithChildren } from 'react'
import React from 'react'

type CircularSkeletonProps = {
  /**
   * The animation style for the skeleton
   * @default 'pulse'
   */
  animation?: SkeletonAnimation
  /**
   * The color scheme of the skeleton
   * @default 'dark'
   */
  color?: SkeletonColor
  /**
   * The size of the skeleton height.
   * Not applicable for circular variant
   */
  height?: never
  /**
   * The size of the skeleton width
   * @default 'medium'
   */
  width?: SkeletonWidth
  /**
   * The shape variant of the skeleton
   * @default 'circle'
   */
  variant?: 'circle'
}

type RectangularSkeletonProps = {
  /**
   * The animation style for the skeleton
   * @default 'pulse'
   */
  animation?: SkeletonAnimation
  /**
   * The color scheme of the skeleton
   * @default 'dark'
   */
  color?: SkeletonColor
  /**
   * The size of the skeleton height.
   * Not applicable for circular variant
   */
  height?: SkeletonHeight
  /**
   * The size of the skeleton width
   * @default 'medium'
   */
  width?: SkeletonWidth
  /**
   * The shape variant of the skeleton
   * @default 'circle'
   */
  variant?: 'rounded' | 'square'
}

type SkeletonAnimation = 'pulse'

type SkeletonAnimations = {
  [key in SkeletonAnimation]: string
}

type SkeletonColor = 'light' | 'dark'

type SkeletonColors = {
  [key in SkeletonColor]: string
}

type SkeletonHeight = 'short' | 'medium' | 'long'

type Heights = {
  [key in SkeletonHeight]: {
    [key in Exclude<SkeletonVariant, 'circle'>]: string
  }
}

type SkeletonProps = CircularSkeletonProps | RectangularSkeletonProps

type SkeletonWidth = 'short' | 'medium' | 'long'

type Widths = {
  [key in SkeletonWidth]: {
    [key in SkeletonVariant]: string
  }
}

type SkeletonVariant = 'circle' | 'rounded' | 'square'

type SkeletonVariants = {
  [key in SkeletonVariant]: string
}

const animations: SkeletonAnimations = {
  pulse: 'animate-pulse',
}

const colors: SkeletonColors = {
  light: 'bg-black-15',
  dark: 'bg-black-25',
}

const heights: Heights = {
  short: {
    square: 'h-4',
    rounded: 'h-4',
  },
  medium: {
    square: 'h-5',
    rounded: 'h-5',
  },
  long: {
    square: 'h-6',
    rounded: 'h-6',
  },
}

const widths: Widths = {
  short: {
    circle: 'w-5 h-5',
    square: 'w-1/3',
    rounded: 'w-1/3',
  },
  medium: {
    circle: 'w-10 h-10',
    square: 'w-1/2',
    rounded: 'w-1/2',
  },
  long: {
    circle: 'w-15 h-15',
    square: 'w-full',
    rounded: 'w-full',
  },
}

const variants: SkeletonVariants = {
  circle: 'rounded-full',
  square: 'rounded-md',
  rounded: 'rounded-full',
}

function Skeleton({
  animation = 'pulse',
  color = 'dark',
  height,
  width,
  variant = 'circle',
}: PropsWithChildren<SkeletonProps>): JSX.Element {
  return (
    <div
      className={clsx(
        'cursor-default justify-center items-center inline-flex',
        animations[animation],
        colors[color],
        variants[variant],
        variant === 'circle'
          ? null
          : height
          ? heights[height][variant]
          : heights.short[variant],
        variant === 'circle'
          ? width
            ? widths[width].circle
            : widths.medium.circle
          : width
          ? widths[width][variant]
          : widths.long[variant],
      )}
      role="presentation"
    />
  )
}

export default Skeleton
